import React, { useState, useEffect } from 'react';
import { FaDownload, FaFilePdf, FaArrowLeft, FaExpand, FaCompress, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { maklumatPelayananService } from '../../services/maklumatPelayananService';

const MaklumatPelayanan = () => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        const result = await maklumatPelayananService.getPublicMaklumat();
        if (result.success) {
            setData(result.data?.data || result.data);
        } else {
            setError(result.message);
        }
        setLoading(false);
    };

    const pdfUrl = data?.file_url || '';

    const handleDownload = () => {
        if (!pdfUrl) return;
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = data.file_name || 'maklumat-pelayanan.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const toggleFullscreen = () => {
        const iframe = document.getElementById('maklumat-pdf-iframe');
        if (iframe) {
            if (!document.fullscreenElement) {
                if (iframe.requestFullscreen) {
                    iframe.requestFullscreen();
                } else if (iframe.webkitRequestFullscreen) {
                    iframe.webkitRequestFullscreen();
                }
                setIsFullscreen(true);
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                }
                setIsFullscreen(false);
            }
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        };
    }, []);

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-12 text-center">
                <FaSpinner className="animate-spin text-4xl text-[#2E7D32] mx-auto mb-4" />
                <p className="text-gray-500">Memuat data...</p>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="container mx-auto px-4 py-12">
                <div className="flex items-center space-x-3 mb-6">
                    <FaFilePdf className="text-4xl text-[#2E7D32]" />
                    <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C3A]">
                        Maklumat Pelayanan
                    </h1>
                </div>
                <div className="card-glass text-center py-12">
                    <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-700 mb-2">Data Belum Tersedia</h2>
                    <p className="text-gray-500">
                        {error || 'Maklumat pelayanan belum diinput oleh admin'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-12">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center space-x-3 mb-2">
                    <FaFilePdf className="text-4xl text-[#2E7D32]" />
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C3A]">
                            Maklumat Pelayanan
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Komitmen BPKAD Kota Magelang dalam memberikan pelayanan terbaik
                        </p>
                    </div>
                </div>
            </div>

            {/* Info dan Tombol Aksi */}
            <div className="card-glass mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start">
                        <div className="w-12 h-12 bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                            <FaFilePdf className="text-white text-xl" />
                        </div>
                        <div>
                            <h3 className="font-bold text-[#0F4C3A] text-lg">{data.judul}</h3>
                            <p className="text-sm text-gray-600">
                                {data.deskripsi || 'File PDF • Dapat diunduh atau dilihat langsung'}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={handleDownload}
                            className="btn-primary flex items-center justify-center"
                        >
                            <FaDownload className="mr-2" />
                            Download PDF
                        </button>
                        <button
                            onClick={toggleFullscreen}
                            className="flex items-center justify-center bg-[#0F4C3A] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#1B5E20] transition-all"
                        >
                            {isFullscreen ? <FaCompress className="mr-2" /> : <FaExpand className="mr-2" />}
                            {isFullscreen ? 'Keluar Fullscreen' : 'Fullscreen'}
                        </button>
                        <Link
                            to="/profil/standar-pelayanan"
                            className="flex items-center justify-center border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            <FaArrowLeft className="mr-2" />
                            Kembali
                        </Link>
                    </div>
                </div>
            </div>

            {/* PDF Viewer */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
                <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-600">
                        <FaFilePdf className="text-red-500 mr-2" />
                        <span className="font-medium">{data.file_name || 'Maklumat Pelayanan'}</span>
                    </div>
                    <span className="text-xs text-gray-400">PDF Viewer</span>
                </div>
                <div className="relative" style={{ height: '80vh', minHeight: '500px' }}>
                    <iframe
                        id="maklumat-pdf-iframe"
                        src={pdfUrl}
                        title={data.judul}
                        className="w-full h-full"
                        style={{ border: 'none' }}
                        loading="lazy"
                    >
                        <p>Browser Anda tidak mendukung tampilan PDF.
                            <a href={pdfUrl} download>Silakan download file PDF di sini.</a>
                        </p>
                    </iframe>
                </div>
            </div>

            {/* Petunjuk */}
            <div className="mt-4 bg-yellow-50 p-4 rounded-lg flex items-start">
                <span className="text-yellow-600 mr-3 text-xl">⚠️</span>
                <div>
                    <p className="text-sm text-yellow-800 font-medium">Jika PDF tidak tampil:</p>
                    <ul className="text-sm text-yellow-700 list-disc list-inside mt-1">
                        <li>Pastikan koneksi internet stabil</li>
                        <li>Klik tombol Download PDF untuk mengunduh dan membukanya dengan pembaca PDF</li>
                        <li>Gunakan browser terbaru (Chrome, Firefox, Edge) untuk dukungan PDF terbaik</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default MaklumatPelayanan;